import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {useAiChatLayout} from '@AiChat/hooks/useAiChatLayout';
import type {AiChatPageClassNames} from '@AiChat/hooks/useAiChatPageClassNames.types';
import {useAiChatSeason} from '@AiChat/hooks/useAiChatSeason';
import {AiChatSeason} from '@AiChat/lib/ai-chat-season-library.types';
import {assertNever} from '@Utils/assert-never.util';
import styles from '@AiChat/ai-chat.module.scss';
import seasonStyles from '@AiChat/ai-chat-season.module.scss';

export function useAiChatPageClassNames(): AiChatPageClassNames {
  const {state} = useAiChat();
  const {isMobileLayout} = useAiChatLayout();
  const season = useAiChatSeason();
  const hasMessages = !!state.currentChatId;
  const isNewUserEmptyState = !hasMessages && !state.isReturningUser;

  const getFullScreenClassName = (): string => {
    const classNames = [styles.fullScreen];

    if (isNewUserEmptyState && !(isMobileLayout && state.isSidebarExpanded)) {
      classNames.push(styles.scrollable);
    }

    return classNames.join(' ');
  };

  const getSeasonPageContentClassName = (): string => {
    if (!season) {
      return '';
    }

    return `${seasonStyles.seasonalPage} ${getSeasonVariablesClassName(season)}`;
  };

  const getSeasonVariablesClassName = (activeSeason: AiChatSeason): string => {
    switch (activeSeason) {
      case AiChatSeason.HALLOWEEN:
        return seasonStyles.halloween;
      default:
        return assertNever(activeSeason);
    }
  };

  const getPageContentClassName = (): string => {
    const classNames = [styles.pageContent, getSeasonPageContentClassName()];

    if (isNewUserEmptyState) {
      classNames.push(styles.scrollable);
    }

    if (state.isAssetsView) {
      classNames.push(styles.assetsView);

      if (isMobileLayout) {
        classNames.push(styles.assetsViewSmall);
      }
    }

    return classNames.join(' ');
  };

  return {
    fullScreen: getFullScreenClassName(),
    pageContent: getPageContentClassName(),
    conversation: styles.conversation,
  };
}
